import { useEffect, useState, useRef } from "react";
import {
  Spinner,
  Table,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Toast,
  ToastBody,
  ToastHeader,
  Button,
} from "reactstrap";
import { Pagination } from "../../Components/Common/Pagination";
import {
  getInsuranceById,
  getAllInsurances,
  updateInsurance,
  deleteInsurance,
  deleteInsuranceImage,
} from "../../slices/thunks";
import { hideErrorMessage } from "../../slices/insurance/reducer";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { useSelector, useDispatch } from "react-redux";
import { Formik } from "formik";
import "./style/index.scss";

const InsuranceTable = ({ searchText }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const fileInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updateInsuranceModal, setUpdateInsuranceModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toasterType, setToasterType] = useState("");
  const [deleteInsuranceModal, setDeleteInsuranceModal] = useState(false);

  const loadingType = useSelector((state) => state.Insurance.loading);
  const insuranceList = useSelector((state) => state.Insurance.insuranceList);
  const insuranceById = useSelector((state) => state.Insurance.insuranceById);

  const toggleUpdateInsuranceModal = () => {
    setUpdateInsuranceModal(!updateInsuranceModal);
  };

  const toggleDeleteInsuranceModal = () => {
    setDeleteInsuranceModal(!deleteInsuranceModal);
  }

  useEffect(() => {
   try {
     dispatch(getAllInsurances(currentPage, pageSize, searchText));
   } catch (error) {}
  }, [dispatch, currentPage, pageSize, searchText]);

  const updateInitialValues = {
    vehicle: insuranceById?.vehicle_data?.number_plate || "",
    type: insuranceById?.vehicle_insurance_type_data?.name || "",
    cost: insuranceById?.cost || "",
    performedOn: insuranceById?.performed_on
      ? moment(insuranceById.performed_on).format("YYYY-MM-DD")
      : "",
    nextDueDate: insuranceById?.next_due_date
      ? moment(insuranceById.next_due_date).format("YYYY-MM-DD")
      : "",
    notes: insuranceById?.notes || "",
    files: insuranceById?.files || [],
    file_uploads: [],
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleUpdateInsuranceSubmit = async (values) => {
    try {
      const formData = new FormData();

      formData.append("type", values.type);
      formData.append("cost", values.cost);
      formData.append("performed_on", values.performedOn);
      formData.append("next_due_date", values.nextDueDate);
      formData.append("notes", values.notes);
      values.file_uploads?.forEach((file, index) => {
        if (file instanceof File) {
          formData.append(`file_uploads[${index}]`, file);
        }
      });
      let response;
      response = await dispatch(updateInsurance(insuranceById.id, formData));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleUpdateInsuranceModal();
      dispatch(getAllInsurances(currentPage, pageSize, searchText));
    }
  };

  const handleDeleteInsurance = async () => {
    try {
      let response;
      response = await dispatch(deleteInsurance(insuranceById.id));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleDeleteInsuranceModal();
      dispatch(getAllInsurances(currentPage, pageSize, searchText));
    }
  };

  const handleDeleteInsuranceImage = async (id) => {
    try {
      let response;
      response = await dispatch(deleteInsuranceImage(id));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
      if (isSuccess) {
        dispatch(getInsuranceById(insuranceById.id));
      }
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      dispatch(getAllInsurances(currentPage, pageSize, searchText));
    }
  };

  return (
    <div className="maintenanceSection">
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
        <Toast isOpen={showToast}>
          <ToastHeader
            icon={toasterType === "danger" ? "danger" : "success"}
            toggle={() => setShowToast(false)}
          >
            {toasterType === "danger" ? "Error" : "Success"}
          </ToastHeader>
          <ToastBody>{toastMessage}</ToastBody>
        </Toast>
      </div>
      <div className="maintanence-table">
        <Table hover responsive>
          <thead>
            <tr>
              <th>Sr.no</th>
              <th>Vehicle</th>
              <th>Type</th>
              <th>Cost</th>
              <th>Performed On</th>
              <th>Next Due Date</th>
              <th>Notes</th>
              <th>File</th>
              <th>Action</th>
            </tr>
          </thead>

          {loadingType.read ? (
            <tbody>
              <tr>
                <td colSpan="8" className="text-center align-middle">
                  <Spinner color="primary" />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {insuranceList?.results?.length ? (
                insuranceList?.results?.map((element, index) => (
                  <tr key={index}>
                    <td>
                      {pageSize * (currentPage - 1) + index + 1}
                    </td>
                    <td>{element.vehicle_data?.number_plate || "-"}</td>
                    <td>{element.vehicle_insurance_type_data?.name || "-"}</td>
                    <td>
                      {element.cost || "-"}
                    </td>
                    <td>{moment(element.performed_on).format("D MMM YYYY") || "-"}</td>
                    <td>{moment(element.next_due_date).format("D MMM YYYY") || "-"}</td>
                    <td title={element.notes}>
                      {element.notes?.length > 10
                        ? `${element.notes.slice(0, 10)}...`
                        : element.notes || "-"}
                    </td>
                    <td>
                      {
                        element.files?.length > 0 ? (
                          <ul className="maintenance-files-list">
                            {element.files?.map((fileItem) => (
                              <li key={fileItem.id}>
                                <a href={`http://dna-django.projectanddemoserver.com/media/insurance_files/${element.id}/${fileItem.file}`} rel="noopener noreferrer" target="_blank">

                                  {(fileItem.file).slice(0, 10) + "..." + (fileItem.file).slice(-3)}
                                </a>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          "-"
                        )
                      }

                    </td>
                    <td>
                      <div className="action-icons">
                        <i
                          className="ri-pencil-fill icon"
                          style={{ color: "#007AFF", fontSize: "20px" }}
                          onClick={() => {
                            dispatch(getInsuranceById(element.id));
                            toggleUpdateInsuranceModal();
                          }}
                        />
                        <i
                          className="ri-delete-bin-fill icon"
                          style={{ color: "#FB0000", fontSize: "20px" }}
                          onClick={() => {
                            dispatch(getInsuranceById(element.id));
                            toggleDeleteInsuranceModal();
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-start text-sm-center" colSpan={8}>
                    <h3>No Insurance Found</h3>
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </Table>
      </div>

      <TripModal
        isModalOpen={updateInsuranceModal}
        toggleModal={toggleUpdateInsuranceModal}
        buttonText={"Update"}
        closeText={"Close without saving"}
        title={"Insurance Management"}
        tripText={"Update Insurance Details"}
        showcloseIcon={true}
        size="lg"
        loading={loadingType.update}
        onSubmit={() => {
          if (formikRef.current) {
            formikRef.current.handleSubmit();
          }
        }}
      >
        <div className="px-3 d-flex gap-4">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={updateInitialValues}
            onSubmit={handleUpdateInsuranceSubmit}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="vehicle" className="passwordLabel">
                        Vehicle Number Plate
                      </Label>
                      <Input
                        type="text"
                        name="vehicle"
                        id="vehicle"
                        value={values.vehicle}
                        readOnly
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label for="type" className="passwordLabel">
                        Maintenance Type
                      </Label>
                      <Input
                        type="text"
                        name="type"
                        id="type"
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label for="cost" className="passwordLabel">
                        Cost
                      </Label>
                      <Input
                        type="text"
                        name="cost"
                        id="cost"
                        value={values.cost}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="performedOn" className="passwordLabel">
                        Performed On
                      </Label>
                      <Input
                        type="date"
                        name="performedOn"
                        id="performedOn"
                        value={values.performedOn}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="nextDueDate" className="passwordLabel">
                        Next Due Date
                      </Label>
                      <Input
                        type="date"
                        name="nextDueDate"
                        id="nextDueDate"
                        value={values.nextDueDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="notes" className="passwordLabel">
                        Notes
                      </Label>
                      <Input
                        type="textarea"
                        name="notes"
                        id="notes"
                        value={values.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="profileInput"
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label className="passwordLabel">
                        Insurance Documents
                      </Label>
                      <ul className="mt-2">
                        {values.files.map((file, index) => (
                          <li
                            key={`existing-${index}`}
                            className="d-flex align-items-center gap-2"
                          >
                            <a
                              href={file.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {file.file.length > 15
                                ? `${file.file.slice(
                                    0,
                                    15
                                  )}...${file.file.slice(-5)}`
                                : file.file}
                            </a>

                            <i
                              className="ri-delete-bin-line text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                if (file.id) {
                                  handleDeleteInsuranceImage(file.id);
                                }
                              }}
                            />
                          </li>
                        ))}

                        {values.file_uploads?.map((file, index) => (
                          <li
                            key={`new-${index}`}
                            className="d-flex align-items-center gap-2"
                          >
                            <span>{file.name}</span>

                            <i
                              className="ri-delete-bin-line text-danger"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                const updated = values.file_uploads.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("file_uploads", updated);
                              }}
                            />
                          </li>
                        ))}
                      </ul>

                      <Col lg={2} md={4}>
                        <div>
                          <Input
                            type="file"
                            innerRef={(el) => (fileInputRef.current = el)}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const currentUploads =
                                  values.file_uploads || [];
                                const isDuplicate = currentUploads.some(
                                  (f) => f.name === file.name
                                );

                                if (!isDuplicate) {
                                  const updatedFiles = [
                                    ...currentUploads,
                                    file,
                                  ];
                                  setFieldValue("file_uploads", updatedFiles);
                                }
                              }
                            }}
                            style={{ display: "none" }}
                            accept=".jpg, .jpeg, .png, .pdf"
                          />
                          <Button
                            className="updateProfile"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Upload <i className="ri-upload-2-line"></i>
                          </Button>
                        </div>
                      </Col>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </div>
      </TripModal>

      <DeleteModal
        show={deleteInsuranceModal}
        loading={loadingType.delete}
        onDeleteClick={() => {
          handleDeleteInsurance();
        }}
        onCloseClick={toggleDeleteInsuranceModal}
      />

      {insuranceList?.results?.length > 0 && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={insuranceList?.count || 0}
        />
      )}
    </div>
  );
};

export default InsuranceTable;
